import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ZoomIn, ChevronLeft, ChevronRight, X, Image as ImageIcon } from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/* Rose Gold palette (strictly aligned with brief) */
const C = {
  roseGold: "#b8977e",
  roseGoldDark: "#a68770",
  cream: "#faf8f5",
  border: "#e8d5c4",
  dark: "#2d2319",
  text: "#5a5a5a",
  overlay: "rgba(13,10,8,0.95)",
};

/* Public gallery is restricted to these 2 sections only */
const PUBLIC_SECTION_SLUGS = ["evenements", "cadres"];

/* Inject global CSS once */
const GalleryStyles = () => (
  <style>{`
    @keyframes galleryFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes lbFade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes lbScale { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }

    .gallery-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
    }
    @media (max-width: 900px) { .gallery-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 520px) { .gallery-grid { grid-template-columns: 1fr; } }

    /* Cadres grid: slightly larger gap, respects 3/2/1 cols too */
    .cadres-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
    }
    @media (max-width: 900px) { .cadres-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 520px) { .cadres-grid { grid-template-columns: 1fr; } }

    .gallery-card {
      position: relative;
      aspect-ratio: 9 / 16;
      border-radius: 8px;
      overflow: hidden;
      cursor: pointer;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      background: #efe7df;
      animation: galleryFade 0.35s ease both;
    }
    .gallery-card.cadres {
      aspect-ratio: 3 / 4;
      border-radius: 4px;
      border: 1px solid ${C.border};
      background: #fff;
    }
    .gallery-card:hover { transform: scale(1.02); box-shadow: 0 20px 40px rgba(0,0,0,0.25); }

    .gallery-card img {
      width: 100%; height: 100%;
      object-fit: cover;
      object-position: center 20%;
      display: block;
      transition: transform 0.4s ease;
    }
    .gallery-card:hover img { transform: scale(1.05); }
    /* Cadres should breathe, never be cropped by an over-zealous cover */
    .gallery-card.cadres img {
      object-fit: contain;
      object-position: center;
      background: #faf8f5;
    }

    .gallery-overlay {
      position: absolute; inset: 0;
      background: rgba(0,0,0,0);
      display: flex; align-items: center; justify-content: center;
      transition: background 0.25s ease;
    }
    .gallery-card:hover .gallery-overlay { background: rgba(0,0,0,0.3); }

    .gallery-zoom {
      opacity: 0;
      color: #fff;
      transform: scale(0.7);
      transition: opacity 0.25s ease, transform 0.25s ease;
      filter: drop-shadow(0 2px 8px rgba(0,0,0,0.4));
    }
    .gallery-card:hover .gallery-zoom { opacity: 1; transform: scale(1); }

    .gallery-title {
      position: absolute;
      left: 0; right: 0; bottom: 0;
      padding: 10px 14px;
      background: linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0));
      color: #fff;
      font-family: 'Montserrat', 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0.01em;
      opacity: 0;
      transition: opacity 0.25s ease;
    }
    .gallery-card:hover .gallery-title { opacity: 1; }

    .pill {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
      border: 1.5px solid ${C.border};
      color: ${C.roseGold};
      background: #fff;
      border-radius: 50px;
      padding: 8px 20px;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .pill:hover { border-color: ${C.roseGold}; }
    .pill.active { background: ${C.roseGold}; color: #fff; border-color: ${C.roseGold}; }
  `}</style>
);

const GalleryPage = () => {
  const navigate = useNavigate();

  const [photos, setPhotos] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all"); // "all" | slug
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Preload neighbour images for smoother lightbox nav
  const preloadCache = useRef(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [photosRes, sectionsRes] = await Promise.all([
          axios.get(`${API}/photos/visible`),
          axios.get(`${API}/photo-sections`),
        ]);
        const allPhotos = Array.isArray(photosRes.data) ? photosRes.data : [];
        // Keep only photos that belong to the 2 public sections
        const publicPhotos = allPhotos
          .filter((p) => PUBLIC_SECTION_SLUGS.includes(p.section))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setPhotos(publicPhotos);

        const allSections = Array.isArray(sectionsRes.data) ? sectionsRes.data : [];
        const publicSections = allSections
          .filter((s) => PUBLIC_SECTION_SLUGS.includes(s.name))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setSections(publicSections);
      } catch (e) {
        console.error("Erreur galerie:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Map slug -> label, for rendering filter pills + lightbox info */
  const labelBySlug = useMemo(() => {
    const m = {};
    sections.forEach((s) => { m[s.name] = s.label; });
    return m;
  }, [sections]);

  const filteredPhotos = useMemo(() => {
    if (activeFilter === "all") return photos;
    return photos.filter((p) => p.section === activeFilter);
  }, [photos, activeFilter]);

  const photoSrc = useCallback((p) => {
    if (!p) return "";
    if (p.file_path && p.file_path.startsWith("http")) return p.file_path;
    if (p.file_path) return `${BACKEND_URL}${p.file_path.startsWith("/") ? "" : "/"}${p.file_path}`;
    return p.url || "";
  }, []);

  const photoTitle = useCallback((p) => {
    if (!p) return "";
    return p.category || p.original_name?.replace(/\.[^.]+$/, "") || "Photo";
  }, []);

  /* Preload neighbours whenever lightbox moves */
  useEffect(() => {
    if (lightboxIndex == null || filteredPhotos.length === 0) return;
    const neighbours = [
      filteredPhotos[(lightboxIndex + 1) % filteredPhotos.length],
      filteredPhotos[(lightboxIndex - 1 + filteredPhotos.length) % filteredPhotos.length],
    ];
    neighbours.forEach((n) => {
      const src = photoSrc(n);
      if (!src || preloadCache.current.has(src)) return;
      const img = new Image();
      img.src = src;
      preloadCache.current.add(src);
    });
  }, [lightboxIndex, filteredPhotos, photoSrc]);

  /* Keyboard navigation + lock body scroll when lightbox open */
  useEffect(() => {
    if (lightboxIndex == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      else if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % filteredPhotos.length);
      else if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + filteredPhotos.length) % filteredPhotos.length);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIndex, filteredPhotos.length]);

  /* Touch / swipe handlers on lightbox image */
  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    if (Math.abs(dx) > 40 && filteredPhotos.length > 1) {
      if (dx < 0) setLightboxIndex((i) => (i + 1) % filteredPhotos.length);
      else        setLightboxIndex((i) => (i - 1 + filteredPhotos.length) % filteredPhotos.length);
    }
    touchStartX.current = null;
  };

  const current = lightboxIndex != null ? filteredPhotos[lightboxIndex] : null;

  return (
    <div
      style={{ background: C.cream, minHeight: "100vh", padding: "80px 40px 60px" }}
      className="font-['DM_Sans']"
      data-testid="gallery-page"
    >
      <GalleryStyles />
      <style>{`
        @media (max-width: 520px) {
          [data-testid="gallery-page"] { padding: 48px 16px 48px !important; }
        }
      `}</style>

      {/* Back link */}
      <div style={{ maxWidth: 1200, margin: "0 auto 32px" }}>
        <button
          onClick={() => navigate("/")}
          data-testid="gallery-back-btn"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "#fff", border: `1.5px solid ${C.border}`,
            color: C.roseGold, borderRadius: 50,
            padding: "8px 18px",
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500,
            cursor: "pointer", transition: "all 0.2s ease",
          }}
        >
          <ArrowLeft size={16} /> Retour
        </button>
      </div>

      {/* Heading */}
      <header style={{ textAlign: "center", marginBottom: 40 }}>
        <h1
          data-testid="gallery-title"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(36px, 5vw, 48px)",
            fontWeight: 400,
            color: C.dark,
            margin: "0 0 8px",
            letterSpacing: "-0.01em",
          }}
        >
          Notre Galerie
        </h1>
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontSize: 22,
            color: C.roseGold,
            margin: 0,
          }}
        >
          Des instants capturés, des souvenirs qui durent
        </p>
      </header>

      {/* Filters */}
      <nav
        data-testid="gallery-filters"
        style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 40 }}
      >
        <button
          className={`pill ${activeFilter === "all" ? "active" : ""}`}
          onClick={() => setActiveFilter("all")}
          data-testid="filter-all"
        >
          Tous
        </button>
        {sections.map((s) => (
          <button
            key={s.name}
            className={`pill ${activeFilter === s.name ? "active" : ""}`}
            onClick={() => setActiveFilter(s.name)}
            data-testid={`filter-${s.name}`}
          >
            {s.label}
          </button>
        ))}
      </nav>

      {/* Grid / Empty state / Loading */}
      <main style={{ maxWidth: 1200, margin: "0 auto" }}>
        {loading ? (
          <p
            style={{ textAlign: "center", color: C.text, fontFamily: "'DM Sans', sans-serif", padding: "60px 0" }}
            data-testid="gallery-loading"
          >
            Chargement…
          </p>
        ) : filteredPhotos.length === 0 ? (
          <div
            data-testid="gallery-empty"
            style={{
              textAlign: "center",
              padding: "80px 24px",
              border: `1px dashed ${C.border}`,
              borderRadius: 16,
              background: "#fff",
            }}
          >
            <ImageIcon size={40} style={{ color: C.roseGold, margin: "0 auto 16px" }} />
            <p
              style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontSize: 20,
                color: C.dark,
                margin: 0,
              }}
            >
              Revenez bientôt — notre galerie se remplit !
            </p>
          </div>
        ) : (() => {
          // Helper: render a single photo card. `globalIdx` is the index
          // into `filteredPhotos` so that lightbox navigation stays linear
          // (events then cadres in "all" view).
          const renderCard = (p, globalIdx) => {
            const isCadre = p.section === "cadres";
            return (
              <article
                key={p.id || p.filename || globalIdx}
                className={`gallery-card${isCadre ? " cadres" : ""}`}
                onClick={() => setLightboxIndex(globalIdx)}
                data-testid={`gallery-card-${globalIdx}`}
              >
                <img src={photoSrc(p)} alt={photoTitle(p)} loading="lazy" />
                <div className="gallery-overlay">
                  <ZoomIn size={44} className="gallery-zoom" />
                </div>
              </article>
            );
          };

          const SectionHeading = ({ icon, title }) => (
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 500,
                fontSize: "clamp(22px, 3vw, 28px)",
                color: C.dark,
                textAlign: "center",
                margin: "0 0 20px",
              }}
            >
              <span style={{ marginRight: 8 }}>{icon}</span>{title}
            </h2>
          );

          const Divider = () => (
            <div style={{ width: 80, height: 2, background: C.roseGold, margin: "56px auto" }} />
          );

          // Single-section filtered views
          if (activeFilter !== "all") {
            const isCadre = activeFilter === "cadres";
            return (
              <div
                className={isCadre ? "cadres-grid" : "gallery-grid"}
                data-testid="gallery-grid"
              >
                {filteredPhotos.map((p, idx) => renderCard(p, idx))}
              </div>
            );
          }

          // "All" view: split & render each section separately with a divider
          const evenements = [];
          const cadres = [];
          filteredPhotos.forEach((p, idx) => {
            const entry = { photo: p, globalIdx: idx };
            if (p.section === "cadres") cadres.push(entry);
            else evenements.push(entry);
          });

          return (
            <div data-testid="gallery-grid">
              {evenements.length > 0 && (
                <>
                  <SectionHeading icon="🎉" title="Nos Événements" />
                  <div className="gallery-grid">
                    {evenements.map(({ photo, globalIdx }) => renderCard(photo, globalIdx))}
                  </div>
                </>
              )}
              {evenements.length > 0 && cadres.length > 0 && <Divider />}
              {cadres.length > 0 && (
                <>
                  <SectionHeading icon="🖼️" title="Cadres Photos" />
                  <div className="cadres-grid">
                    {cadres.map(({ photo, globalIdx }) => renderCard(photo, globalIdx))}
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </main>

      {/* CTA bottom */}
      <section
        style={{ textAlign: "center", marginTop: 72, paddingTop: 32, borderTop: `1px solid ${C.border}` }}
        data-testid="gallery-cta"
      >
        <p
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 22,
            color: C.dark,
            margin: "0 0 20px",
          }}
        >
          Votre événement mérite d'être immortalisé ✨
        </p>
        <button
          onClick={() => navigate("/booking")}
          data-testid="gallery-reserve-btn"
          style={{
            background: C.roseGold,
            color: "#fff",
            border: "none",
            borderRadius: 50,
            padding: "14px 36px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 4px 20px rgba(184,151,126,0.35)",
            transition: "all 0.25s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.roseGoldDark; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = C.roseGold; }}
        >
          Réserver votre Photobooth →
        </button>
      </section>

      {/* Lightbox */}
      {current && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxIndex(null)}
          data-testid="lightbox-overlay"
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: C.overlay,
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "lbFade 0.25s ease",
          }}
        >
          {/* Close */}
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            aria-label="Fermer"
            data-testid="lightbox-close"
            style={{
              position: "absolute", top: 20, right: 20, zIndex: 2,
              width: 44, height: 44, borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
              border: `1px solid ${C.roseGold}`,
              color: C.roseGold, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.roseGold; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = C.roseGold; }}
          >
            <X size={20} />
          </button>

          {/* Prev */}
          {filteredPhotos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + filteredPhotos.length) % filteredPhotos.length); }}
              aria-label="Photo précédente"
              data-testid="lightbox-prev"
              style={{
                position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", zIndex: 2,
                width: 52, height: 52, borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: `1px solid ${C.roseGold}`,
                color: C.roseGold, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = C.roseGold; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = C.roseGold; }}
            >
              <ChevronLeft size={26} />
            </button>
          )}

          {/* Image + info */}
          <div
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="lightbox-content"
            style={{
              // Cadres are typically 4:3 landscape → give them more horizontal room
              // Événements are portrait 9:16 → a narrower viewport keeps them tall
              maxWidth: current.section === "cadres" ? "min(92vw, 1100px)" : "min(92vw, 720px)",
              maxHeight: "92vh",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
              animation: "lbScale 0.3s ease",
            }}
          >
            <img
              src={photoSrc(current)}
              alt={photoTitle(current)}
              data-testid="lightbox-img"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: current.section === "cadres" ? 4 : 8,
                boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
                background: current.section === "cadres" ? "#faf8f5" : "transparent",
              }}
            />
            <div
              data-testid="lightbox-info"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: "#fff",
                textAlign: "center",
              }}
            >
              {current.section && labelBySlug[current.section] && (
                <p style={{ margin: 0, fontSize: 12, color: C.roseGold, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {labelBySlug[current.section]}
                </p>
              )}
              {filteredPhotos.length > 1 && (
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "rgba(255,255,255,0.45)" }}>
                  {lightboxIndex + 1} / {filteredPhotos.length}
                </p>
              )}
            </div>
          </div>

          {/* Next */}
          {filteredPhotos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % filteredPhotos.length); }}
              aria-label="Photo suivante"
              data-testid="lightbox-next"
              style={{
                position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", zIndex: 2,
                width: 52, height: 52, borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: `1px solid ${C.roseGold}`,
                color: C.roseGold, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = C.roseGold; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = C.roseGold; }}
            >
              <ChevronRight size={26} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
